<template>
  <div class="page-container">
    <PageHeader title="意见反馈">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openDialog">提交反馈</el-button>
      </template>
    </PageHeader>

    <!-- 搜索面板 -->
    <SearchPanel>
      <el-select v-model="query.feedbackType" clearable placeholder="反馈类型" size="small" @change="fetchList">
        <el-option label="功能建议" :value="1" />
        <el-option label="问题报错" :value="2" />
        <el-option label="体验优化" :value="3" />
        <el-option label="其他" :value="4" />
      </el-select>
      <el-select v-model="query.sfStatus" clearable placeholder="处理状态" size="small" @change="fetchList">
        <el-option label="待处理" :value="1" />
        <el-option label="处理中" :value="2" />
        <el-option label="已解决" :value="3" />
        <el-option label="已关闭" :value="4" />
        <el-option label="已驳回" :value="5" />
      </el-select>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="fetchList">查询</el-button>
      </template>
    </SearchPanel>

    <!-- 反馈列表 -->
    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 30, 50]"
        :total="total"
        row-id-key="sfId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
      />
      <EmptyState v-if="!loading && list.length === 0" title="暂无反馈记录" description="点击右上角「提交反馈」按钮，对系统提出您的建议" icon="el-icon-chat-line-round" />
    </TablePanel>

    <!-- 查看详情弹窗 -->
    <el-dialog title="反馈详情" :visible.sync="detailDialogVisible" width="600px" append-to-body>
      <template v-if="currentRow">
        <div class="fb-detail-info">
          <div class="fb-detail-row">
            <span class="fb-detail-label">反馈编号</span>
            <span class="fb-detail-value">{{ currentRow.feedbackNo || '-' }}</span>
          </div>
          <div class="fb-detail-row">
            <span class="fb-detail-label">反馈类型</span>
            <span class="fb-detail-value">
              <span class="fb-type-tag" :class="'fb-type--' + currentRow.feedbackType">{{ feedbackTypeMap[currentRow.feedbackType] || '其他' }}</span>
            </span>
          </div>
          <div class="fb-detail-row">
            <span class="fb-detail-label">处理状态</span>
            <span class="fb-detail-value">
              <span class="fb-status-tag" :class="'fb-status--' + currentRow.sfStatus">{{ statusMap[currentRow.sfStatus] || '未知' }}</span>
            </span>
          </div>
          <div class="fb-detail-row">
            <span class="fb-detail-label">提交时间</span>
            <span class="fb-detail-value">{{ currentRow.createtime || '-' }}</span>
          </div>
          <div class="fb-detail-row fb-detail-row--block">
            <span class="fb-detail-label">反馈内容</span>
            <span class="fb-detail-value fb-detail-value--pre">{{ currentRow.feedbackContent || '-' }}</span>
          </div>
          <!-- 反馈图片 -->
          <template v-if="parseAttachmentUrls(currentRow.feedbackAttachment).length">
            <div class="fb-detail-row fb-detail-row--block">
              <span class="fb-detail-label">反馈图片</span>
              <span class="fb-detail-value">
                <div class="fb-detail-images">
                  <el-image
                    v-for="(url, idx) in parseAttachmentUrls(currentRow.feedbackAttachment)"
                    :key="idx"
                    :src="url"
                    fit="cover"
                    class="fb-detail-thumb"
                    :preview-src-list="parseAttachmentUrls(currentRow.feedbackAttachment)"
                  />
                </div>
              </span>
            </div>
          </template>
          <!-- 管理员回复 -->
          <template v-if="currentRow.handleResult">
            <div class="fb-detail-row fb-detail-row--block" style="margin-top: 14px;">
              <span class="fb-detail-label">管理员回复</span>
              <span class="fb-detail-value fb-detail-value--pre">{{ currentRow.handleResult }}</span>
            </div>
            <div class="fb-detail-row">
              <span class="fb-detail-label">回复人</span>
              <span class="fb-detail-value">{{ currentRow.handlerName || '-' }}</span>
            </div>
            <div class="fb-detail-row">
              <span class="fb-detail-label">回复时间</span>
              <span class="fb-detail-value">{{ currentRow.handletime || '-' }}</span>
            </div>
          </template>
        </div>
      </template>
      <template #footer>
        <el-button size="small" @click="detailDialogVisible = false">关 闭</el-button>
      </template>
    </el-dialog>

    <!-- 提交反馈弹窗 -->
    <el-dialog title="提交反馈" :visible.sync="dialogVisible" width="560px" :close-on-click-modal="false" append-to-body>
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="80px">
        <el-form-item label="反馈类型" prop="feedbackType">
          <el-radio-group v-model="form.feedbackType">
            <el-radio :label="1">功能建议</el-radio>
            <el-radio :label="2">问题报错</el-radio>
            <el-radio :label="3">体验优化</el-radio>
            <el-radio :label="4">其他</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="反馈内容" prop="feedbackContent">
          <el-input v-model="form.feedbackContent" type="textarea" :rows="5" placeholder="请详细描述您的建议或遇到的问题…" maxlength="500" show-word-limit />
        </el-form-item>
        <!-- 图片上传 -->
        <el-form-item label="上传图片">
          <aj-upload
            ref="uploadRef"
            multiple
            :limit="5"
            accept-type="image/*"
            :max-file-size="10 * 1024 * 1024"
            :upload-request="uploadToCos"
            upload-btn-text="选择图片"
            :history-files="form.attachmentFiles"
            @upload-success="handleUploadSuccess"
          />
          <div class="fb-upload-tip">支持 JPG、PNG、GIF 格式，单张不超过 10MB，最多 5 张</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="handleSubmit">提交反馈</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
// 用户反馈页面：提交反馈（含图片上传）、查看自己的反馈列表
import { addSysFeedback, getSysFeedbackListUser } from '@/api/api'
import { uploadToCos } from '@/utils/cosUpload'
import AjUpload from '@/components/ajUpload/ajUpload.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

const FEEDBACK_TYPE_MAP = { 1: '功能建议', 2: '问题报错', 3: '体验优化', 4: '其他' }
const STATUS_MAP = { 1: '待处理', 2: '处理中', 3: '已解决', 4: '已关闭', 5: '已驳回' }

export default {
  name: 'FeedbackPage',
  components: { AjUpload, AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      submitLoading: false,
      dialogVisible: false,
      detailDialogVisible: false,
      currentRow: null,
      list: [],
      total: 0,
      query: { feedbackType: '', sfStatus: '', currentPage: 1, pageSize: 10 },
      form: { feedbackType: 1, feedbackContent: '', attachmentFiles: [] },
      formRules: {
        feedbackType: [{ required: true, message: '请选择反馈类型', trigger: 'change' }],
        feedbackContent: [{ required: true, message: '请输入反馈内容', trigger: 'blur' }]
      },
      feedbackTypeMap: FEEDBACK_TYPE_MAP,
      statusMap: STATUS_MAP,
      // 上传到腾讯云 COS
      uploadToCos
    }
  },
  computed: {
    // 表格列配置
    columns () {
      return [
        { type: 'index', label: '序号', width: 55 },
        { prop: 'feedbackNo', label: '反馈编号', width: 170, showTooltip: true },
        {
          prop: 'feedbackType',
          label: '反馈类型',
          width: 100,
          render: (h, { row }) => {
            const typeMap = { 1: '功能建议', 2: '问题报错', 3: '体验优化', 4: '其他' }
            const colorMap = { 1: '#2563eb', 2: '#dc2626', 3: '#059669', 4: '#7c3aed' }
            const bgMap = { 1: '#eff6ff', 2: '#fef2f2', 3: '#ecfdf5', 4: '#f5f3ff' }
            const borderMap = { 1: '#bfdbfe', 2: '#fecaca', 3: '#a7f3d0', 4: '#c4b5fd' }
            const t = row.feedbackType || 4
            return h('span', {
              style: {
                display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', color: colorMap[t], background: bgMap[t], border: `1px solid ${borderMap[t]}`
              }
            }, typeMap[t] || '其他')
          }
        },
        { prop: 'feedbackContent', label: '反馈内容', minWidth: 220, showTooltip: true },
        {
          prop: 'sfStatus',
          label: '处理状态',
          width: 100,
          render: (h, { row }) => {
            const statusMap = { 1: '待处理', 2: '处理中', 3: '已解决', 4: '已关闭', 5: '已驳回' }
            const colorMap = { 1: '#d97706', 2: '#2563eb', 3: '#059669', 4: '#64748b', 5: '#e11d48' }
            const bgMap = { 1: '#fff7ed', 2: '#eff6ff', 3: '#ecfdf5', 4: '#f1f5f9', 5: '#fff1f2' }
            const s = row.sfStatus || 1
            return h('span', {
              style: {
                display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', color: colorMap[s], background: bgMap[s]
              }
            }, statusMap[s] || '未知')
          }
        },
        { prop: 'createtime', label: '提交时间', width: 150 },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 100,
          fixed: 'right',
          buttons: () => [{ key: 'view', text: '查看', type: 'primary' }]
        }
      ]
    }
  },
  created () {
    this.fetchList()
  },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const params = {}
        if (this.query.feedbackType) params.feedbackType = this.query.feedbackType
        if (this.query.sfStatus) params.sfStatus = this.query.sfStatus
        params.currentPage = this.query.currentPage
        params.pageSize = this.query.pageSize
        const res = await getSysFeedbackListUser(params)
        const data = res.data
        if (Array.isArray(data)) {
          this.list = data
          this.total = res.total || data.length
        } else if (data && data.records) {
          this.list = data.records
          this.total = data.total || 0
        } else {
          this.list = []
          this.total = 0
        }
      } catch (e) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    resetQuery () {
      this.query = { feedbackType: '', sfStatus: '', currentPage: 1, pageSize: 10 }
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },
    // 操作按钮分发
    handleOperateBtnClick ({ btn, row }) {
      if (btn.key === 'view') this.openDetail(row)
    },
    // 查看详情
    openDetail (row) {
      this.currentRow = { ...row }
      this.detailDialogVisible = true
    },
    openDialog () {
      this.form = { feedbackType: 1, feedbackContent: '', attachmentFiles: [] }
      this.dialogVisible = true
      this.$nextTick(() => { if (this.$refs.formRef) this.$refs.formRef.clearValidate() })
    },
    // 上传成功回调：更新附件文件列表
    handleUploadSuccess (files) {
      this.form.attachmentFiles = files
    },
    // 解析附件 JSON 为 URL 数组
    parseAttachmentUrls (attachmentStr) {
      if (!attachmentStr) return []
      try {
        const parsed = JSON.parse(attachmentStr)
        return Array.isArray(parsed) ? parsed.filter(Boolean) : []
      } catch (e) {
        return []
      }
    },
    async handleSubmit () {
      try {
        await this.$refs.formRef.validate()
      } catch { return }
      this.submitLoading = true
      try {
        // 提取已上传图片的 URL，转为 JSON 字符串传给后端
        const attachmentUrls = (this.form.attachmentFiles || [])
          .filter(f => f && f.url)
          .map(f => f.url)
        const submitData = {
          feedbackType: this.form.feedbackType,
          feedbackContent: this.form.feedbackContent
        }
        if (attachmentUrls.length) {
          submitData.feedbackAttachment = JSON.stringify(attachmentUrls)
        }
        await addSysFeedback(submitData)
        this.$message.success('反馈提交成功，感谢您的建议！')
        this.dialogVisible = false
        this.fetchList()
      } catch (e) {
        this.$message.error(e.message || '提交失败，请稍后重试')
      } finally {
        this.submitLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// 反馈类型标签
.fb-type-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}
.fb-type--1 { color: #2563eb; background: #eff6ff; border: 1px solid #bfdbfe; }
.fb-type--2 { color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; }
.fb-type--3 { color: #059669; background: #ecfdf5; border: 1px solid #a7f3d0; }
.fb-type--4 { color: #7c3aed; background: #f5f3ff; border: 1px solid #c4b5fd; }

// 处理状态标签
.fb-status-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}
.fb-status--1 { color: #d97706; background: #fff7ed; }
.fb-status--2 { color: #2563eb; background: #eff6ff; }
.fb-status--3 { color: #059669; background: #ecfdf5; }
.fb-status--4 { color: #64748b; background: #f1f5f9; }
.fb-status--5 { color: #e11d48; background: #fff1f2; }

// 内容列截断
.fb-content-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
}

// 详情弹窗信息区
.fb-detail-info {
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.fb-detail-row {
  display: flex;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
}
.fb-detail-row--block {
  flex-direction: column;
}
.fb-detail-label {
  width: 80px;
  flex-shrink: 0;
  padding: 10px 14px;
  font-size: 13px;
  color: #64748b;
  background: #f8fafc;
}
.fb-detail-value {
  flex: 1;
  padding: 10px 14px;
  font-size: 14px;
  color: #1e293b;
  word-break: break-all;
}
.fb-detail-value--pre {
  white-space: pre-wrap;
  line-height: 1.7;
}

// 详情弹窗图片缩略图
.fb-detail-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.fb-detail-thumb {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: scale(1.06);
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12);
  }
}

// 操作列按钮间距
::v-deep .aj-table-operation-link {
  margin-right: 10px;
}
::v-deep .aj-table-operation-link:last-child {
  margin-right: 0;
}

// 上传提示
.fb-upload-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}
</style>
